/**
 * Cart badge animation logic:
 * - The badge fades in when cartTotalItems > 0 (items in cart).
 * - The badge fades out ONLY when cartTotalItems transitions from >0 to 0.
 *   This is tracked using a ref and state to detect the transition.
 * - If the cart was already zero, no badge or animation is shown.
 * This ensures a smooth and meaningful animation only when items are removed from the cart.
 */
import { trans } from "@mongez/localization";
import { cartAtom } from "core/cart";
import { cn } from "core/shared";
import { userAtom } from "core/user";
import { useEffect, useRef, useState } from "react";
import { Cart } from "~/design-system/components";
import { headerMenuAtom, searchAtom } from "~/modules/shared/atoms";
import { cartDrawerAtom } from "~/modules/shared/atoms/cart-drawer.atom";
import { HeaderButton } from "./HeaderButton";

export function CartButton() {
  const userTotalCart = userAtom.use("totalCart");

  const cart = cartAtom.use("cart");

  const cartTotalItems = userTotalCart || (cart && cart.items?.length) || 0;

  const prevTotalItems = useRef(cartTotalItems);

  const [showFadeOut, setShowFadeOut] = useState(false);

  useEffect(() => {
    if (prevTotalItems.current > 0 && cartTotalItems === 0) {
      setShowFadeOut(true);
      setTimeout(() => setShowFadeOut(false), 200);
    }
    prevTotalItems.current = cartTotalItems;
  }, [cartTotalItems]);

  return (
    <HeaderButton
      onClick={() => {
        cartDrawerAtom.toggle();
        headerMenuAtom.close();
        searchAtom.close();
      }}
      data-ignore-outside-clicks
      title={trans("cart.title")}
      aria-label={trans("cart.title")}
    >
      <div className="relative">
        <Cart className="size-7" />
        {cartTotalItems > 0 && (
          <div
            className={cn(
              "animate-fade-in absolute -top-0 -right-0 size-[10px] rounded-full border bg-[#FF667E]",
            )}
          />
        )}
        {showFadeOut && (
          <div
            className={cn(
              "animate-fade-out absolute -top-0 right-0 size-[10px] rounded-full border bg-[#FF667E]",
            )}
          />
        )}
      </div>
    </HeaderButton>
  );
}
